class SearchModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.onBodyClickEvent = this.onBodyClick.bind(this);
    this.onDocumentKeydownEvent = this.onDocumentKeydown.bind(this);

    if (!this.detailsContainer || !this.summaryToggle) return;

    this.detailsContainer.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
    });

    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));

    // Capture close clicks before predictive-search or other theme scripts can stop propagation.
    this.addEventListener('click', (event) => {
      const closeTarget = event.target.closest('[data-k9-search-close], .k9w-search-panel__close, .search__button[type="button"]');
      if (!closeTarget || !this.contains(closeTarget)) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }, true);

    const resetButton = this.querySelector('button[type="reset"]');
    if (resetButton) resetButton.addEventListener('click', this.reset.bind(this));

    // Route clear navigational searches directly to the matching K9 Loft page.
    // This prevents Shopify's generic search ranking from sending terms such as
    // "locations" to product results when the visitor clearly wants a site page.
    const searchForm = this.querySelector('.k9w-search-panel__form');
    if (searchForm) {
      searchForm.addEventListener('submit', (event) => {
        const input = searchForm.querySelector('input[type="search"]');
        const query = this.normalizeSearchQuery(input ? input.value : '');
        const destination = this.getK9SearchDestination(query);
        if (!destination) return;
        event.preventDefault();
        window.location.assign(destination);
      }, true);
    }

    this.summaryToggle.setAttribute('role', 'button');
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.detailsContainer.hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.detailsContainer?.hasAttribute('open')) return;
    if (!this.contains(event.target)) this.close();
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape' || event.code === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    // V371: keep Tab inside the open search dialog. The list of focusable controls is
    // read at each key press, so it stays correct as predictive results appear and change.
    if (event.key === 'Tab') this.keepFocusInside(event);
  }

  keepFocusInside(event) {
    const panel = this.detailsContainer && this.detailsContainer.querySelector('[role="dialog"]');
    if (!panel || !this.detailsContainer.hasAttribute('open')) return;
    const focusable = Array.from(panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
    )).filter((el) => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (!panel.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && (active === first || active === panel.querySelector('[tabindex="-1"]'))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open() {
    if (!this.detailsContainer) return;
    if (typeof setScrollbarWidth === 'function') setScrollbarWidth();
    if (typeof setHeaderBottomPosition === 'function') setHeaderBottomPosition();

    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.addEventListener('keydown', this.onDocumentKeydownEvent, true);
    document.body.classList.add('search-modal--open');

    const focusContainer = this.detailsContainer.querySelector('[tabindex="-1"]');
    const input = this.detailsContainer.querySelector('input:not([type="hidden"])');
    if (typeof trapFocus === 'function' && focusContainer && input) trapFocus(focusContainer, input);
  }

  close() {
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    if (this.detailsContainer) this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.removeEventListener('keydown', this.onDocumentKeydownEvent, true);
    document.body.classList.remove('search-modal--open');
    document.documentElement.classList.remove('search-modal--open');
    document.dispatchEvent(new CustomEvent('searchmodal:close'));

    // Restore focus to the search trigger when possible.
    if (this.summaryToggle && document.activeElement && this.contains(document.activeElement)) {
      this.summaryToggle.focus({ preventScroll: true });
    }
  }

  normalizeSearchQuery(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getK9SearchDestination(query) {
    if (!query) return null;

    // Theme-editor routes take priority. Each block can contain comma, line-break,
    // semicolon, or pipe-separated synonyms and one destination URL.
    const editableRoutes = Array.from(this.querySelectorAll('[data-k9-search-route]'));
    for (const route of editableRoutes) {
      const destination = route.dataset.destination;
      const terms = String(route.dataset.terms || '')
        .split(/[\n,;|]+/)
        .map((term) => this.normalizeSearchQuery(term))
        .filter(Boolean);
      if (destination && terms.includes(query)) return destination;
    }

    // Built-in fallback routes remain in place so search still works if a
    // merchant removes every editable route block.
    const routes = [
      { url: '/pages/locations', terms: ['location', 'locations', 'store', 'stores', 'address', 'addresses', 'where are you', 'find a location', 'find your loft', 'echo park', 'sherman oaks', 'pasadena'] },
      { url: '/pages/pricing', terms: ['price', 'prices', 'pricing', 'rates', 'rate'] },
      { url: '/pages/webcams', terms: ['webcam', 'webcams', 'camera', 'cameras', 'dog cam', 'dog cams'] },
      { url: '/pages/daycare', terms: ['daycare', 'day care', 'dog daycare'] },
      { url: '/pages/boarding', terms: ['boarding', 'board', 'overnight', 'overnights', 'dog boarding'] },
      { url: '/pages/grooming', terms: ['grooming', 'groom', 'haircut', 'bath'] },
      { url: '/pages/dental', terms: ['dental', 'dental clinic', 'teeth cleaning', 'teeth', 'non anesthetic dental', 'non-anesthetic dental'] },
      { url: '/pages/coupons', terms: ['coupon', 'coupons', 'offer', 'offers', 'monthly coupons', 'promotion', 'promotions', 'promo', 'promos'] },
      { url: '/pages/request-a-visit', terms: ['book', 'booking', 'book now', 'plan a visit', 'request a visit', 'appointment', 'appointments', 'reservation', 'reservations'] },
      { url: '/pages/about-us', terms: ['about', 'about us', 'our story'] },
      { url: '/pages/faqs', terms: ['faq', 'faqs', 'frequently asked questions', 'questions'] },
      { url: '/pages/careers', terms: ['career', 'careers', 'job', 'jobs', 'hiring', 'employment'] },
      { url: '/blogs/news', terms: ['news', 'blog', 'blogs'] }
    ];

    for (const route of routes) {
      if (route.terms.includes(query)) return route.url;
    }

    return null;
  }

  reset(event) {
    event.preventDefault();
    const searchInput = this.querySelector('input[type="search"]');
    if (searchInput) {
      searchInput.value = '';
      searchInput.focus();
    }
  }

  disconnectedCallback() {
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.removeEventListener('keydown', this.onDocumentKeydownEvent, true);
  }
}

if (!customElements.get('search-modal')) {
  customElements.define('search-modal', SearchModal);
}
